import { createSlice } from '@reduxjs/toolkit';
import get from 'lodash/get';
import { getStatusMessage } from '../../utils/response-code-messages';
import Snackbar from '../../common/snackBar';
import { SearchtActionTypes, UseCasesDataRequestActionTypes } from '../models/use-cases';

const page_name = 'buildUseCase';

export interface useCaseStates {
  isUseCasesListLoading: boolean;
  isUseCaseDeployed: boolean;
  useCasesList: Array<any>;
  useCasesListTotalRowCount: number;
  useCasesListPageNumber: number;
  useCasesListError: boolean;
  isUseCaseDataLoading: boolean;
  selectedUseCase: Object;
  isAddUseCaseLoading: boolean;
  isAddUseCaseSuccess: boolean;
  isAddUseCaseError: boolean;
  isRemoveUseCaseSuccess: boolean;
  isRemoveUseCaseError: boolean;
  isUpdateUseCaseSuccess: boolean;
  isUpdateUseCaseError: boolean;
  useCaseErrorMessage: string;
  isUpdateUseCaseStatusError: boolean;
  isUpdateUseCaseStatusSuccess: boolean;
  approver: boolean;
  searchItem: undefined | string;
}

const initialState: useCaseStates = {
  isUseCasesListLoading: false,
  useCasesList: [],
  useCasesListTotalRowCount: 0,
  useCasesListPageNumber: 1,
  useCasesListError: false,
  isUseCaseDataLoading: false,
  selectedUseCase: {},
  isAddUseCaseLoading: false,
  isUseCaseDeployed: false,
  isAddUseCaseSuccess: false,
  isAddUseCaseError: false,
  isRemoveUseCaseSuccess: false,
  isRemoveUseCaseError: false,
  isUpdateUseCaseSuccess: false,
  isUpdateUseCaseError: false,
  useCaseErrorMessage: '',
  isUpdateUseCaseStatusError: false,
  isUpdateUseCaseStatusSuccess: false,
  approver: false,
  searchItem: '',
};

const BuildUseCaseReducer = createSlice({
  name: page_name,
  initialState: initialState,
  reducers: {
    getAllUseCasesDataRequest: (state, action: UseCasesDataRequestActionTypes) => {
      state.isUseCasesListLoading = true;

      state.isAddUseCaseSuccess = false;
      state.isRemoveUseCaseSuccess = false;
      state.isUpdateUseCaseSuccess = false;
    },
    getAllUseCasesDataResponse: (state, action: any) => {
      state.isUseCasesListLoading = false;
      if (state.searchItem === get(action, 'payload.res.search_item', '')) {
        state.approver = get(action, 'payload.res.approver', false);
        state.useCasesList = get(action, 'payload.res.data', []);
        state.useCasesListTotalRowCount = get(action, 'payload.res.total_count', 0);
        state.useCasesListPageNumber = get(action, 'payload.res.page', 0);
      }
      // state.approver = action.payload.approver;
      // state.factoriesList = get(action, 'payload.data', []);
    },
    getAllUseCasesDataFailed: (state, action: any) => {
      state.isUseCasesListLoading = false;
      state.useCasesListError = true;
      state.useCasesList = [];

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message);
    },
    getUseCaseDataRequest: (state, action) => {
      state.isUseCaseDataLoading = true;
    },
    getUseCaseDataResponse: (state, action) => {
      state.isUseCaseDataLoading = false;
      state.selectedUseCase = action.payload.res;
    },
    getUseCaseDataFailed: (state, action) => {
      state.isUseCaseDataLoading = false;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message);
    },
    addUseCaseRequest: (state) => {
      state.isUseCasesListLoading = true;
      state.isAddUseCaseLoading = true;
      state.isAddUseCaseSuccess = false;
      state.isAddUseCaseError = false;
    },
    addUseCaseResponse: (state, action: any) => {
      state.isUseCasesListLoading = false;
      state.isAddUseCaseLoading = false;
      state.isAddUseCaseSuccess = true;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.success(message);
    },
    addUseCaseFailed: (state, action: any) => {
      state.isUseCasesListLoading = false;
      state.isAddUseCaseLoading = false;
      state.isAddUseCaseError = true;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message);
    },
    updateUseCaseRequest: (state) => {
      state.isUseCasesListLoading = true;
      state.isUpdateUseCaseSuccess = false;
      state.isUpdateUseCaseError = false;
    },
    updateUseCaseResponse: (state, action: any) => {
      state.isUseCasesListLoading = false;
      state.isUpdateUseCaseSuccess = true;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.success(message);
    },
    updateUseCaseFailed: (state, action: any) => {
      state.isUseCasesListLoading = false;
      state.isUpdateUseCaseError = true;
      state.isUpdateUseCaseSuccess = false;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message);
    },
    updateUseCaseStatusRequest: (state) => {
      state.isUseCasesListLoading = true;
      state.isUpdateUseCaseStatusSuccess = false;
      state.isUpdateUseCaseStatusError = false;
    },
    updateUseCaseStatusResponse: (state, action: any) => {
      state.isUseCasesListLoading = false;
      state.isUpdateUseCaseStatusSuccess = true;
      const message = getStatusMessage(page_name, action.payload);
      Snackbar.success(message);
    },
    updateUseCaseStatusFailed: (state, action: any) => {
      state.isUseCasesListLoading = false;
      state.isUpdateUseCaseStatusError = true;
      state.isUpdateUseCaseStatusSuccess = false;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message);
    },
    deployUseCaseRequest: (state, action: any) => {
      state.isUseCasesListLoading = true;
      state.isUseCaseDeployed = true;
      state.isUpdateUseCaseSuccess = false;
      state.isUpdateUseCaseError = false;
    },
    removeUseCaseRequest: (state) => {
      state.isUseCasesListLoading = true;
      state.isRemoveUseCaseSuccess = false;
      state.isRemoveUseCaseError = false;
    },
    removeUseCaseResponse: (state, action: any) => {
      state.isUseCasesListLoading = false;
      state.isRemoveUseCaseSuccess = true;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.success(message);
    },
    removeUseCaseFailed: (state, action: any) => {
      state.isUseCasesListLoading = false;
      state.isRemoveUseCaseError = true;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message);
    },
    resetSelectedUseCase: (state) => {
      state.selectedUseCase = {};
    },
    resetLoadingState: (state) => {
      state.isUseCasesListLoading = false;
      state.isAddUseCaseSuccess = false;
      state.isRemoveUseCaseSuccess = false;
      state.isUpdateUseCaseSuccess = false;
      state.isAddUseCaseError = false;
      state.useCasesListError = false;
      state.isRemoveUseCaseError = false;
      state.isUpdateUseCaseError = false;
      state.isUseCaseDeployed = false;
      state.useCaseErrorMessage = '';

      state.isUpdateUseCaseStatusSuccess = false;
      state.isUpdateUseCaseStatusError = false;
    },
    resetUsecaseDate: (state) => {
      state.useCasesList = [];
      state.useCasesListTotalRowCount = 0;
      state.useCasesListPageNumber = 1;
      state.searchItem = '';
    },
    onSearchForItem: (state, action: SearchtActionTypes) => {
      state.searchItem = action.payload;
    },
  },
});

export const buildUseCaseActions = BuildUseCaseReducer.actions;
export default BuildUseCaseReducer;
